
import {
  computed, defineComponent, nextTick, onMounted, PropType, ref, watch
} from 'vue';
import { templateRef, useElementSize } from '@vueuse/core';
import useResponsiveness from '@/mixins/useResponsiveness';
import LandingpageService from '@/services/LandingpageService';
import LPUserCard from './LPUserCard.vue';
import LPUserCarouselArrow from './LPUserCarouselArrow.vue';
import { UserCarouselUser } from './userCarousel.types';

const CARD_WIDTH = 344;
const CARD_GAP = 60;
const MOBILE_CARD_GAP = 20;
const OUTER_NAV_CLEARANCE = 240;
const MAX_QUEUED_CLICKS = 1;
const TWO_CARD_MIN_WIDTH = (2 * CARD_WIDTH) + CARD_GAP + OUTER_NAV_CLEARANCE;
const THREE_CARD_MIN_WIDTH = (3 * CARD_WIDTH) + (2 * CARD_GAP) + OUTER_NAV_CLEARANCE;

const wrapIndex = (index: number, length: number): number => {
  if (!length) return 0;
  return ((index % length) + length) % length;
};

export default defineComponent({
  name: 'LPUserCarousel',
  components: {
    LPUserCard,
    LPUserCarouselArrow
  },
  props: {
    users: {
      type: Array as PropType<UserCarouselUser[]>,
      default: () => []
    }
  },
  emits: ['visibility-change'],
  setup(props, { emit }) {
    const { isLandingPageMobileWidth, isLandingPageTabletWidth } = useResponsiveness();
    const fetchedUsers = ref<UserCarouselUser[]>([]);
    const currentIndex = ref(0);
    const pendingDirection = ref<-1 | 0 | 1>(0);
    const queuedDirections = ref<Array<-1 | 1>>([]);
    const isAnimating = ref(false);
    const transitionEnabled = ref(false);
    const trackTranslate = ref(0);
    const root = templateRef<HTMLElement>('root');
    const { width: carouselWidth } = useElementSize(root);
    const resolvedUsers = computed(() => (
      props.users?.length ? props.users : fetchedUsers.value
    ));

    watch(resolvedUsers, (users) => {
      emit('visibility-change', users.length > 0);
    }, { immediate: true });

    const visibleCardCount = computed(() => {
      if (isLandingPageMobileWidth.value) return 1;

      if (!carouselWidth.value) {
        return isLandingPageTabletWidth.value ? 2 : 3;
      }

      if (carouselWidth.value >= THREE_CARD_MIN_WIDTH) return 3;
      if (carouselWidth.value >= TWO_CARD_MIN_WIDTH) return 2;
      return 1;
    });

    const visibleUsers = computed(() => {
      const totalUsers = resolvedUsers.value.length;
      const amount = Math.min(visibleCardCount.value, totalUsers);
      const startIndex = wrapIndex(currentIndex.value, totalUsers);

      return Array.from({ length: amount }, (_, offset) => {
        const userIndex = wrapIndex(startIndex + offset, totalUsers);
        return resolvedUsers.value[userIndex];
      });
    });

    const stepWidth = computed(() => CARD_WIDTH + (isLandingPageMobileWidth.value ? MOBILE_CARD_GAP : CARD_GAP));

    const slidingUsers = computed(() => {
      const totalUsers = resolvedUsers.value.length;
      const amount = visibleUsers.value.length;

      if (!totalUsers || !amount) {
        return [];
      }

      const startIndex = wrapIndex(currentIndex.value - 1, totalUsers);

      return Array.from({ length: amount + 2 }, (_, offset) => {
        const userIndex = wrapIndex(startIndex + offset, totalUsers);
        return resolvedUsers.value[userIndex];
      });
    });

    const hasMultipleUsers = computed(() => resolvedUsers.value.length > 1);
    const showInnerNavigation = computed(() => isLandingPageMobileWidth.value && hasMultipleUsers.value);
    const showOuterNavigation = computed(() => !isLandingPageMobileWidth.value && hasMultipleUsers.value);
    const trackWidth = computed(() => (
      (visibleUsers.value.length * CARD_WIDTH) + ((visibleUsers.value.length - 1) * CARD_GAP)
    ));
    const baseOffset = computed(() => -stepWidth.value);

    watch(baseOffset, (value) => {
      if (!isAnimating.value) {
        trackTranslate.value = value;
      }
    }, { immediate: true });

    const triggerSlide = async (direction: -1 | 1) => {
      if (!hasMultipleUsers.value) {
        return;
      }

      if (isAnimating.value) {
        if (queuedDirections.value.length < MAX_QUEUED_CLICKS) {
          queuedDirections.value.push(direction);
        }
        return;
      }

      pendingDirection.value = direction;
      transitionEnabled.value = false;
      trackTranslate.value = baseOffset.value;
      await nextTick();
      isAnimating.value = true;

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          transitionEnabled.value = true;
          trackTranslate.value = baseOffset.value + (-direction * stepWidth.value);
        });
      });
    };

    const showNext = () => {
      triggerSlide(1);
    };

    const showPrevious = () => {
      triggerSlide(-1);
    };

    const isVisibleCard = (index: number) => index > 0 && index <= visibleUsers.value.length;

    const handleCardClick = (index: number) => {
      if (!isVisibleCard(index) || !hasMultipleUsers.value) {
        return;
      }

      if (visibleUsers.value.length === 1) {
        showNext();
        return;
      }

      if (index === 1) {
        showPrevious();
        return;
      }

      showNext();
    };

    const handleTransitionEnd = (event: TransitionEvent) => {
      if (event.propertyName !== 'transform') {
        return;
      }

      if (!isAnimating.value || !pendingDirection.value) {
        return;
      }

      transitionEnabled.value = false;
      currentIndex.value = wrapIndex(currentIndex.value + pendingDirection.value, resolvedUsers.value.length);
      trackTranslate.value = baseOffset.value;
      pendingDirection.value = 0;
      isAnimating.value = false;

      const nextQueuedDirection = queuedDirections.value.shift();
      if (nextQueuedDirection) {
        requestAnimationFrame(() => {
          triggerSlide(nextQueuedDirection);
        });
      }
    };

    onMounted(async () => {
      if (props.users?.length) {
        return;
      }

      try {
        const users = await LandingpageService.getRandomLandingpageUsers();
        fetchedUsers.value = users;
      } catch {
        fetchedUsers.value = [];
      }
    });

    return {
      handleTransitionEnd,
      isAnimating,
      isVisibleCard,
      handleCardClick,
      root,
      slidingUsers,
      trackWidth,
      trackTranslate,
      transitionEnabled,
      visibleUsers,
      showInnerNavigation,
      showOuterNavigation,
      showNext,
      showPrevious
    };
  }
});
